"use client";

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useRouter, usePathname } from "next/navigation";

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  must_change_password: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithToken: (accessToken: string, refreshToken?: string) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}

async function tryRefreshToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem("cl_refresh_token");
  if (!refreshToken) return null;
  try {
    const res = await fetch("/api/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    localStorage.setItem("cl_token", data.access_token);
    if (data.refresh_token) {
      localStorage.setItem("cl_refresh_token", data.refresh_token);
    }
    return data.access_token;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();
  const pathname = usePathname();

  const fetchUser = useCallback(async (accessToken: string): Promise<boolean> => {
    try {
      const res = await fetch("/api/users/me", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        setToken(accessToken);
        return true;
      }
      // Access token failed — try refresh before giving up
      if (res.status === 401) {
        const newToken = await tryRefreshToken();
        if (newToken) {
          const retryRes = await fetch("/api/users/me", {
            headers: { Authorization: `Bearer ${newToken}` },
          });
          if (retryRes.ok) {
            const data = await retryRes.json();
            setUser(data);
            setToken(newToken);
            return true;
          }
        }
      }
      throw new Error("Failed to fetch user");
    } catch {
      localStorage.removeItem("cl_token");
      localStorage.removeItem("cl_refresh_token");
      setUser(null);
      setToken(null);
      return false;
    }
  }, []);

  const [tokenChecked, setTokenChecked] = useState(false);

  useEffect(() => {
    // Skip token validation if SSO callback tokens are in the URL —
    // the login page's SSO handler will process them via loginWithToken
    if (typeof window !== "undefined" && new URLSearchParams(window.location.search).has("token")) {
      setIsLoading(false);
      setTokenChecked(true);
      return;
    }
    const savedToken = localStorage.getItem("cl_token");
    if (savedToken) {
      fetchUser(savedToken).finally(() => {
        setIsLoading(false);
        setTokenChecked(true);
      });
    } else {
      setIsLoading(false);
      setTokenChecked(true);
    }
  }, [fetchUser]);

  // Redirect unauthenticated users to login — only after token check is fully complete
  useEffect(() => {
    if (!tokenChecked) return;
    const publicPaths = ["/", "/login", "/setup", "/features", "/how-it-works", "/law-firms", "/in-house", "/security", "/why-contract-lucidity"];
    if (!user && !publicPaths.includes(pathname) && !pathname.startsWith("/D365")) {
      router.replace("/login");
    }
  }, [tokenChecked, user, pathname, router]);

  const loginWithToken = useCallback(async (accessToken: string, refreshToken?: string): Promise<boolean> => {
    localStorage.setItem("cl_token", accessToken);
    if (refreshToken) {
      localStorage.setItem("cl_refresh_token", refreshToken);
    }
    const success = await fetchUser(accessToken);
    if (!success) {
      localStorage.removeItem("cl_token");
      localStorage.removeItem("cl_refresh_token");
    }
    setIsLoading(false);
    setTokenChecked(true);
    return success;
  }, [fetchUser]);

  const login = async (email: string, password: string) => {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Login failed");
    }
    const data = await res.json();
    localStorage.setItem("cl_token", data.access_token);
    localStorage.setItem("cl_refresh_token", data.refresh_token);
    setToken(data.access_token);
    await fetchUser(data.access_token);

    // Check if setup is complete
    try {
      const setupRes = await fetch("/api/admin/setup/status");
      if (setupRes.ok) {
        const setupData = await setupRes.json();
        if (!setupData.wizard_completed) {
          router.replace("/setup");
          return;
        }
      }
    } catch {}

    router.replace("/dashboard");
  };

  const refreshUser = useCallback(async () => {
    const savedToken = localStorage.getItem("cl_token");
    if (savedToken) await fetchUser(savedToken);
  }, [fetchUser]);

  const logout = () => {
    localStorage.removeItem("cl_token");
    localStorage.removeItem("cl_refresh_token");
    setUser(null);
    setToken(null);
    router.replace("/login");
  };

  return (
    <AuthContext.Provider value={{ user, token, isLoading, login, loginWithToken, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}
